import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
} from '@mui/material';
import { Settings, Person } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { resetAllUserPoints } from '../utils/adminUtils';
import { updateUserPointsWithLevelUpDetection } from '../../../../utils/levelUpNotifications';

const PointManagement = ({ userPoints, onPointsUpdated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pointsDialog, setPointsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPointsValue, setNewPointsValue] = useState('');

  const handleSetPoints = async () => {
    if (!selectedUser || newPointsValue === '') return;
    
    try {
      const points = parseInt(newPointsValue);
      if (isNaN(points) || points < 0) {
        enqueueSnackbar('Please enter a valid number', { variant: 'error' });
        return;
      }
      
      const userData = userPoints[selectedUser];
      
      // Use the level-up detection function
      await updateUserPointsWithLevelUpDetection(
        userData.uid, 
        points, 
        enqueueSnackbar, 
        selectedUser
      );
      
      // Show regular success message (level-up notification is handled automatically)
      enqueueSnackbar(`Set ${selectedUser}'s points to ${points}`, { variant: 'success' });
      
      // Refresh user points
      if (onPointsUpdated) {
        await onPointsUpdated();
      }
      
      setPointsDialog(false);
      setSelectedUser(null);
      setNewPointsValue('');
    } catch (error) {
      enqueueSnackbar('Error setting points', { variant: 'error' });
    }
  };

  const handleResetAllPoints = async () => {
    if (!window.confirm('Are you sure you want to reset all points to 0? This cannot be undone.')) {
      return;
    }
    
    try {
      await resetAllUserPoints();
      enqueueSnackbar('All points reset to 0', { variant: 'success' });
      if (onPointsUpdated) {
        await onPointsUpdated();
      }
    } catch (error) {
      enqueueSnackbar('Error resetting points', { variant: 'error' });
    }
  };

  const openPointsDialog = (userName) => {
    setSelectedUser(userName);
    setNewPointsValue(userPoints[userName]?.points?.toString() || '0');
    setPointsDialog(true);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Box
          sx={{
            bgcolor: '#f8f9fa',
            borderRadius: 4,
            p: 4,
            minHeight: '60vh'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              pb: 3,
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Settings sx={{ color: '#666', fontSize: 28 }} />
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#333',
                    fontFamily: '"Roboto", sans-serif'
                  }}
                >
                  Point Management
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#666',
                  fontSize: '1rem',
                  opacity: 0.8
                }}
              >
                Manage user points and experience levels
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              onClick={handleResetAllPoints}
              startIcon={<Settings />}
              sx={{
                bgcolor: '#dc3545',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                '&:hover': { 
                  bgcolor: '#c82333',
                  boxShadow: '0 6px 16px rgba(220, 53, 69, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Reset All Points
            </Button>
          </Box>

          {/* User Points Grid */}
          <Grid container spacing={3}>
            {Object.entries(userPoints).map(([name, data]) => (
              <Grid item xs={12} sm={6} md={4} key={name}>
                <Card
                  sx={{
                    background: 'white',
                    borderRadius: 3,
                    minHeight: '240px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: '#1976d2',
                            color: 'white',
                            mr: 2,
                            width: 40,
                            height: 40
                          }}
                        >
                          <Person />
                        </Avatar>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: '#333'
                          }}
                        >
                          {name}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            color: '#1976d2',
                            fontWeight: 'bold',
                            mb: 1
                          }}
                        >
                          {data.points} pts
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#666',
                            mb: 2
                          }}
                        >
                          Level {data.level} • {Math.max(0, Math.min(100, data.xpProgress || 0))}% XP
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, Math.min(100, data.xpProgress || 0))}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#e3f2fd',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#1976d2',
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openPointsDialog(name)}
                      sx={{
                        color: '#1976d2',
                        borderColor: '#1976d2',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        '&:hover': { 
                          borderColor: '#1565c0',
                          bgcolor: '#e3f2fd'
                        }
                      }}
                    >
                      Set Points
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </motion.div>

      {/* Points Management Dialog */}
      <Dialog
        open={pointsDialog}
        onClose={() => {
          setPointsDialog(false);
          setSelectedUser(null);
          setNewPointsValue('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Set Points for {selectedUser}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Points"
            type="number"
            fullWidth
            variant="outlined"
            value={newPointsValue}
            onChange={(e) => setNewPointsValue(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPointsDialog(false);
              setSelectedUser(null);
              setNewPointsValue('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSetPoints}
            variant="contained"
            color="primary"
          >
            Set Points
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PointManagement;